import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  IconButton,
  Popover,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DeleteConfirmationModal from "../deleteConfirmationModal";
import { isMobile } from "../../globalStyles/mobile";
import DeleteIcon from "../../globalStyles/icons/deleteIcon";
import EditIcon from "../../globalStyles/icons/editIcon";
import ViewIcon from "../../globalStyles/icons/vieewIcon";
import { agentConfigService } from "../../utils/services/agentConfigService";
import AgentConfigDetailsModal from "../agent-config-details-modal";
import { toast } from "react-toastify";

const StyledTableCell = styled(TableCell)({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "var(--table-header-bg)",
    border: "none",
    padding: 4,
  },
  [`&.${tableCellClasses.body}`]: { fontSize: 12, padding: 6 },
});

const StyledTableRow = styled(TableRow)(() => ({
  "&:nth-of-type(odd)": {
    backgroundColor: "var(--bg-paper)",
    border: "1px solid var(--border-color)",
  },
  "&:nth-of-type(even)": {
    backgroundColor: "var(--row-bg-even)",
    border: "1px solid var(--border-color)",
  },
  "&:lastChild td, &:lastChild th": { border: 0 },
}));

const ActionPopover = ({ row, onView, onEdit, onDelete }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const openPopover = (e) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };
  const closePopover = () => setAnchorEl(null);

  const doAction = (type) => {
    closePopover();
    if (type === "view") onView(row);
    if (type === "edit") onEdit(row);
    if (type === "delete") onDelete(row);
  };

  return (
    <Box>
      <IconButton
        onClick={openPopover}
        sx={{ color: open ? "primary.main" : "inherit" }}
      >
        <MoreVertIcon />
      </IconButton>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={closePopover}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Box display="flex" flexDirection="column" p={1}>
          <Button
            onClick={() => doAction("edit")}
            sx={{ textTransform: "none" }}
          >
            <Box
              display="flex"
              alignItems="center"
              justifyContent="flex-start"
              width="100%"
              gap={1}
            >
              <EditIcon />
              <Typography
                fontFamily="Poppins"
                fontSize={16}
                fontWeight={500}
                color="var(--text-tertiary)"
              >
                Edit
              </Typography>
            </Box>
          </Button>
          <Button
            onClick={() => doAction("view")}
            sx={{ textTransform: "none" }}
          >
            <Box
              display="flex"
              alignItems="center"
              justifyContent="flex-start"
              width="100%"
              gap={1}
            >
              <ViewIcon />
              <Typography
                fontFamily="Poppins"
                fontSize={16}
                fontWeight={500}
                color="var(--text-tertiary)"
              >
                View
              </Typography>
            </Box>
          </Button>
          <Button
            onClick={() => doAction("delete")}
            sx={{ textTransform: "none" }}
          >
            <Box
              display="flex"
              alignItems="center"
              justifyContent="flex-start"
              width="100%"
              gap={1}
            >
              <DeleteIcon />
              <Typography
                fontFamily="Poppins"
                fontSize={16}
                fontWeight={500}
                color="var(--text-tertiary)"
              >
                Delete
              </Typography>
            </Box>
          </Button>
        </Box>
      </Popover>
    </Box>
  );
};

const AgentConfigList = ({
  data,
  onEditData,
  onDeleteData,
  viewMode,
  selectedAgent,
  setSelectedAgent,
  currentPage,
  itemsPerPage,
}) => {
  const [rows, setRows] = useState([]);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [openView, setOpenView] = useState(false);
  const startIndex = currentPage * itemsPerPage;

  const openDelete = (row) => {
    setSelectedAgent(row);
    setOpenConfirm(true);
  };

  const confirmDelete = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await agentConfigService.delete(selectedAgent._id);
      onDeleteData?.();
      toast.success("Agent config deleted successfully");
      setOpenConfirm(false);
    } catch {
      toast.error("Delete failed");
    } finally {
      setLoading(false);
    }
  };

  const openViewModal = (row) => {
    setSelectedAgent(row);
    setOpenView(true);
  };

  useEffect(() => {
    setRows(
      data?.map((c) => ({
        ...c,
        action: (
          <ActionPopover
            row={c}
            onView={openViewModal}
            onEdit={onEditData}
            onDelete={openDelete}
          />
        ),
      }))
    );
  }, [data]);

  const gridView = () => (
    <Grid
      container
      spacing={2}
      mt={1}
      pb={5}
      sx={{ maxHeight: "100%", overflow: "auto" }}
    >
      {rows?.map((c, i) => (
        <Grid item xs={12} sm={6} md={4} key={c._id}>
          <Card
            sx={{
              border: "1px solid var(--border-color)",
              borderRadius: 1,
              boxShadow: 3,
              height: "100%",
              position: "relative",
            }}
          >
            <CardContent>
              <Box display="flex" justifyContent="space-between">
                <Typography
                  fontFamily="Poppins"
                  fontWeight={500}
                  color="var(--text-tertiary)"
                >
                  {startIndex + i + 1}
                </Typography>
                <Box position="absolute" top={10} right={10}>
                  {c.action}
                </Box>
              </Box>

              <Grid container spacing={1} mt={2}>
                <Grid item xs={5}>
                  <Typography
                    fontFamily="Poppins"
                    fontWeight={500}
                    color="var(--text-tertiary)"
                    textAlign={"start"}
                  >
                    Name
                  </Typography>
                </Grid>
                <Grid item xs={7}>
                  <Tooltip title={c.name}>
                    <Typography
                      fontFamily="Poppins"
                      fontWeight={500}
                      color="var(--text-tertiary)"
                      noWrap
                      textAlign={"start"}
                    >
                      : {c.name}
                    </Typography>
                  </Tooltip>
                </Grid>

                <Grid item xs={5}>
                  <Typography
                    fontFamily="Poppins"
                    fontWeight={500}
                    color="var(--text-tertiary)"
                    textAlign={"start"}
                  >
                    Agent ID
                  </Typography>
                </Grid>
                <Grid item xs={7}>
                  <Tooltip title={c.id}>
                    <Typography
                      fontFamily="Poppins"
                      fontWeight={500}
                      color="var(--text-tertiary)"
                      noWrap
                      textAlign={"start"}
                    >
                      : {c.id}
                    </Typography>
                  </Tooltip>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );

  const listView = () => (
    <TableContainer
      sx={{ overflowY: "auto", maxHeight: "100%", paddingX: 0.4 }}
    >
      <Table stickyHeader sx={{ border: "1px solid var(--border-color)" }}>
        <TableHead>
          <StyledTableRow>
            <StyledTableCell>
              <Typography
                fontSize={14}
                fontWeight={500}
                fontFamily="Poppins"
                color="var(--text-tertiary)"
              >
                SL NO
              </Typography>
            </StyledTableCell>
            <StyledTableCell>
              <Typography
                fontSize={14}
                fontWeight={500}
                fontFamily="Poppins"
                color="var(--text-tertiary)"
              >
                NAME
              </Typography>
            </StyledTableCell>
            <StyledTableCell>
              <Typography
                fontSize={14}
                fontWeight={500}
                fontFamily="Poppins"
                color="var(--text-tertiary)"
              >
                AGENT ID
              </Typography>
            </StyledTableCell>
            <StyledTableCell />
          </StyledTableRow>
        </TableHead>
        <TableBody>
          {rows?.map((c, i) => (
            <StyledTableRow key={c._id}>
              <StyledTableCell>
                <Typography
                  fontSize={12}
                  fontFamily="Poppins"
                  color="var(--text-secondary)"
                >
                  {startIndex + i + 1}
                </Typography>
              </StyledTableCell>
              <StyledTableCell>
                <Tooltip title={c.name}>
                  <Typography
                    fontSize={12}
                    fontFamily="Poppins"
                    color="var(--text-secondary)"
                    noWrap
                    sx={{
                      maxWidth: 200,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {c.name}
                  </Typography>
                </Tooltip>
              </StyledTableCell>
              <StyledTableCell>
                <Tooltip title={c.id}>
                  <Typography
                    fontSize={12}
                    fontFamily="Poppins"
                    color="var(--text-secondary)"
                    noWrap
                  >
                    {c.id}
                  </Typography>
                </Tooltip>
              </StyledTableCell>
              <StyledTableCell sx={{ width: "3%", textAlign: "center" }}>
                {c.action}
              </StyledTableCell>
            </StyledTableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box sx={{ width: "100%" }}>
      {loading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          position="fixed"
          top={0}
          left={0}
          right={0}
          bottom={0}
          bgcolor="rgba(0,0,0,0.5)"
          zIndex={9999}
          color="#fff"
        >
          <CircularProgress color="inherit" />
        </Box>
      ) : (
        <Box
          sx={{
            overflowY: "scroll",
            display: "flex",
            maxHeight: isMobile() ? "60vh" : "calc(100vh - 250px)",
            width: "100%",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            "&::-webkit-scrollbar": { width: 0 },
          }}
        >
          {viewMode === "grid" ? gridView() : listView()}
        </Box>
      )}

      <DeleteConfirmationModal
        title={`Are you sure you want to delete ${selectedAgent?.name} ?`}
        open={openConfirm}
        handleClose={() => setOpenConfirm(false)}
        handleDelete={confirmDelete}
      />

      <AgentConfigDetailsModal
        agent={selectedAgent}
        open={openView}
        onClose={() => setOpenView(false)}
      />
    </Box>
  );
};

export default AgentConfigList;
